import { api, API_BASE } from "./client";
import axios from "axios";

/**
 * Phase 7.1 — Inventory share-link API client.
 *
 * Admin endpoints use the standard authed `api` instance. Public endpoints
 * use a bare axios call (no auth header) so the same module can power both
 * the creator's modal and the anonymous viewer page.
 */

const adminBase = "/inventory/share-links";

// ── Param builder ────────────────────────────────────────────────────────
// Mirrors `buildParams` from `@/api/inventory` so the adapter below can
// drive the existing private inventory components without changes.
function buildParams(filters = {}, extra = {}) {
  const p = {};
  if (filters.preset && filters.preset !== "custom") {
    p.preset = filters.preset;
  } else if (filters.preset === "custom") {
    if (filters.date_from) p.date_from = filters.date_from;
    if (filters.date_to) p.date_to = filters.date_to;
  }
  if (filters.source_report) p.source_reports = filters.source_report;
  if (filters.transaction_types?.length) {
    p.canonical_types = filters.transaction_types.join(",");
  }
  if (filters.include_non_warehouse) p.include_non_warehouse = true;
  return { ...p, ...extra };
}

const inventoryShareApi = {
  // ── Admin ────────────────────────────────────────────────────────
  async create({ label, enabledSections, defaultFilters, includeNonWarehouse, expiresInDays }) {
    const { data } = await api.post(adminBase, {
      label: label || null,
      enabled_sections: enabledSections,
      default_filters: defaultFilters || {},
      include_non_warehouse: !!includeNonWarehouse,
      expires_in_days: expiresInDays ?? null,
    });
    return data;
  },
  async list() {
    const { data } = await api.get(adminBase);
    return data;
  },
  async revoke(token) {
    const { data } = await api.delete(`${adminBase}/${encodeURIComponent(token)}`);
    return data;
  },

  // ── Public (no auth header) ──────────────────────────────────────
  async getPublicMeta(token) {
    const { data } = await axios.get(
      `${API_BASE}/public/inventory/share/${encodeURIComponent(token)}`
    );
    return data;
  },
  async getPublicSection(token, section, params = {}) {
    const cleanParams = Object.fromEntries(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "")
    );
    const { data } = await axios.get(
      `${API_BASE}/public/inventory/share/${encodeURIComponent(token)}/data/${section}`,
      { params: cleanParams }
    );
    return data;
  },

  buildPublicUrl(token) {
    return `${window.location.origin}/public/inventory/${encodeURIComponent(token)}`;
  },
};

/**
 * Adapter that mirrors the `@/api/inventory` module shape but routes every
 * call through the public dispatcher endpoint guarded by `token`. Used by
 * `PublicInventoryPage` to drive the existing private sub-components
 * (InventorySummaryTable, …) without duplicating their JSX.
 *
 * Export functions intentionally omitted: the public view hides export
 * buttons and never invokes them.
 */
export function buildPublicApiAdapter(token) {
  const fetchSection = (section, params) =>
    inventoryShareApi.getPublicSection(token, section, params);

  return {
    async getKpis(filters = {}, extra = {}) {
      return fetchSection("kpis", buildParams(filters, extra));
    },
    async getSummaryTable(filters = {}, opts = {}) {
      return fetchSection(
        "summary-table",
        buildParams(filters, {
          page: opts.page || 1,
          page_size: opts.pageSize || 50,
          sort_by: opts.sortBy || "salidas_qty",
          sort_dir: opts.sortDir || "desc",
          search: opts.search || undefined,
        })
      );
    },
    async getMonthlyPivot(filters = {}, opts = {}) {
      return fetchSection(
        "monthly-pivot",
        buildParams(filters, {
          page: opts.page || 1,
          page_size: opts.pageSize || 50,
          sort_by: opts.sortBy || "total_year",
          sort_dir: opts.sortDir || "desc",
          search: opts.search || undefined,
        })
      );
    },
    async getDeadStock({
      filters = {},
      page = 1,
      pageSize = 50,
      sortBy = "dias_sin_movimiento",
      sortDir = "desc",
      search,
      thresholdDays = 90,
    } = {}) {
      const params = buildParams(filters, {
        page,
        page_size: pageSize,
        sort_by: sortBy,
        sort_dir: sortDir,
        search: search || undefined,
        threshold_days: thresholdDays,
      });
      // dead-stock ignora preset/canonical_types (igual que el cliente privado).
      delete params.canonical_types;
      delete params.preset;
      delete params.date_from;
      delete params.date_to;
      return fetchSection("dead-stock", params);
    },
    async getUserMovements(filters = {}, opts = {}) {
      return fetchSection(
        "user-movements",
        buildParams(filters, {
          page: opts.page || 1,
          page_size: opts.pageSize || 50,
          sort_by: opts.sortBy || "total_tx",
          sort_dir: opts.sortDir || "desc",
          search: opts.search || undefined,
        })
      );
    },
    async getTopProducts(filters = {}, opts = {}) {
      return fetchSection(
        "top-products",
        buildParams(filters, {
          metric: opts.metric || "qty",
          top_n: opts.topN || 25,
        })
      );
    },
    async getChartsData(filters = {}) {
      return fetchSection("charts-data", buildParams(filters));
    },
    async getNeedsReview({ page = 1, pageSize = 50, canonicalType = null } = {}) {
      const params = { page, page_size: pageSize };
      if (canonicalType) params.canonical_type = canonicalType;
      return fetchSection("needs-review", params);
    },
    // Export functions are intentionally absent — public view hides them.
  };
}

export default inventoryShareApi;
